import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'user' | 'premium' | 'admin'
  language: 'fr' | 'en' | 'wo' | 'ln' | 'sw' | 'ff'
  avatar?: string
}

export interface Agent {
  id: string
  name: string
  type: string
  description: string
  avatar: string
  status: 'idle' | 'thinking' | 'responding' | 'error'
}

export interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  message: string
  timestamp: Date
  read: boolean
}

interface AppState {
  // User
  user: User | null
  setUser: (user: User | null) => void
  
  // Authentication
  isAuthenticated: boolean
  accessToken: string | null
  refreshToken: string | null
  setAuth: (token: string, user: User) => void
  setRefreshToken: (token: string) => void
  logout: () => void
  
  // UI State
  sidebarOpen: boolean
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  
  // Theme
  theme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark') => void
  
  // Language
  language: 'fr' | 'en' | 'wo' | 'ln' | 'sw' | 'ff'
  setLanguage: (lang: 'fr' | 'en' | 'wo' | 'ln' | 'sw' | 'ff') => void
  
  // Active Agent
  activeAgent: Agent | null
  setActiveAgent: (agent: Agent | null) => void
  
  // Notifications
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void
  removeNotification: (id: string) => void
  markNotificationRead: (id: string) => void
  clearNotifications: () => void
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // SECURITY: no default user, no default session. Authentication is
      // only granted after a successful login against the backend.
      user: null,
      setUser: (user) => set({ user }),

      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      setAuth: (accessToken, user) => 
        set({ accessToken, user, isAuthenticated: true }),
      setRefreshToken: (refreshToken) => set({ refreshToken }),
      logout: () => {
        localStorage.removeItem('refresh_token');
        set({ accessToken: null, refreshToken: null, user: null, isAuthenticated: false });
      },
      
      // UI State
      sidebarOpen: true,
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      
      // Theme
      theme: 'dark',
      setTheme: (theme) => set({ theme }),
      
      // Language
      language: 'fr',
      setLanguage: (language) => set({ language }),
      
      // Active Agent
      activeAgent: null,
      setActiveAgent: (activeAgent) => set({ activeAgent }),
      
      // Notifications
      notifications: [],
      addNotification: (notification) => 
        set((state) => ({
          notifications: [
            {
              ...notification,
              id: crypto.randomUUID(),
              timestamp: new Date(),
              read: false,
            },
            ...state.notifications,
          ].slice(0, 50), // Keep only last 50
        })),
      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),
      markNotificationRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        })),
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: 'gback-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        theme: state.theme,
        language: state.language,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
)

